import { useQuery } from '@tanstack/react-query';
import { CalendarClock, ClipboardList, Download, HandCoins, UsersRound, WalletCards } from 'lucide-react';
import { useState } from 'react';
import { BranchPicker } from '../components/ui/BranchPicker';
import { EmptyState } from '../components/ui/EmptyState';
import { Select } from '../components/ui/Select';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useRegions } from '../lib/options';
import { apiErrorMessage } from '../lib/ToastContext';
import { Loader } from '../components/ui/Spinner';

const money = (v: string) => `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayDate = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const thisMonth = () => monthStart().slice(0, 7);
const monthLabel = (period: string) => new Date(`${period}T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

type AttendanceRow = { workDate: string; employeeCode: string; name: string; branchName: string; checkInAt: string | null; checkOutAt: string | null; status: string };
type LeaveTaken = { employeeCode: string; name: string; branchName: string; leaveType: string; startDate: string; endDate: string; days: string };
type LeaveBalance = { employeeCode: string; name: string; branchName: string; leaveType: string; entitled: string; carriedForward: string; used: string; remaining: string };
type SalaryRow = { periodMonth: string; employeeCode: string; name: string; branchName: string | null; payableDays: string; lopDays: string; grossEarnings: string; totalDeductions: string; netPay: string };
type LoanRow = { employeeCode: string; name: string; branchName: string; type: 'loan' | 'advance'; principalAmount: string; balance: string; status: string; startMonth: string };
type StatutoryRow = { periodMonth: string; employeeCode: string; name: string; branchName: string | null; pfWageBase: string; pfEmployeeAmount: string; pfEmployerAmount: string; esiWageBase: string; esiEmployeeAmount: string; esiEmployerAmount: string; ptState: string | null; ptAmount: string; lwfEmployeeAmount: string; lwfEmployerAmount: string };
type HeadcountJoin = { employeeCode: string; name: string; branchName: string | null; joiningDate: string };
type HeadcountExit = { employeeCode: string; name: string; branchName: string | null; exitDate: string };

const time = (v: string | null) => (v ? new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—');
function csvHref(path: string, params: Record<string, string | undefined>) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
  qs.set('format', 'csv');
  return `${api.defaults.baseURL}${path}?${qs.toString()}`;
}

function BranchRegionFilters({ branchId, setBranchId, regionId, setRegionId }: { branchId: string; setBranchId: (v: string) => void; regionId: string; setRegionId: (v: string) => void }) {
  const regions = useRegions();
  return (
    <>
      <label className="label">Branch<BranchPicker clearLabel="All branches" value={branchId} onChange={setBranchId} /></label>
      <label className="label">Region<Select className="mt-1" value={regionId} onChange={(e) => setRegionId(e.target.value)}><option value="">All regions</option>{regions.data?.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</Select></label>
    </>
  );
}

function ExportLink({ href }: { href: string }) {
  return <a className="btn-secondary" href={href} target="_blank" rel="noreferrer"><Download size={16} /> Export CSV</a>;
}

const REGISTER_STATUSES = [
  { value: 'present', label: 'Present' },
  { value: 'late', label: 'Late' },
  { value: 'half_day', label: 'Half day' },
  { value: 'on_leave', label: 'On leave' },
  { value: 'absent', label: 'Absent' },
  { value: 'holiday', label: 'Holiday' },
] as const;
const REGISTER_LABELS: Record<string, string> = { ...Object.fromEntries(REGISTER_STATUSES.map((x) => [x.value, x.label])), not_checked_in: 'Not checked in yet' };
const REGISTER_BADGE: Record<string, string> = { absent: 'bg-red-50 text-red-700', not_checked_in: 'bg-amber-50 text-amber-800', on_leave: 'bg-blue-50 text-blue-700', late: 'bg-amber-50 text-amber-800' };
// Same rule as the server's status filter: "Absent" includes today's not-yet-checked-in staff.
const matchesRegisterStatus = (rowStatus: string, filter: string) => !filter || rowStatus === filter || (filter === 'absent' && rowStatus === 'not_checked_in');

function AttendanceRegisterTab() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayDate());
  const [branchId, setBranchId] = useState('');
  const [regionId, setRegionId] = useState('');
  const [status, setStatus] = useState('');
  const params = { from, to, branchId: branchId || undefined, regionId: regionId || undefined };
  // Fetched unfiltered so the per-status counts stay visible; the export applies the status filter server-side.
  const report = useQuery({ queryKey: ['reports', 'attendance-register', params], queryFn: async () => (await api.get<AttendanceRow[]>('/api/reports/attendance-register', { params })).data });
  const all = report.data ?? [];
  const rows = all.filter((r) => matchesRegisterStatus(r.status, status));
  const counts = REGISTER_STATUSES.map((x) => ({ ...x, count: all.filter((r) => matchesRegisterStatus(r.status, x.value)).length })).filter((x) => x.count > 0);
  return (
    <div>
      <div className="card mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <label className="label">From<input type="date" className="field mt-1" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="label">To<input type="date" className="field mt-1" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <BranchRegionFilters branchId={branchId} setBranchId={setBranchId} regionId={regionId} setRegionId={setRegionId} />
        <label className="label">Status<Select className="mt-1" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All statuses</option>{REGISTER_STATUSES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}</Select></label>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <ExportLink href={csvHref('/api/reports/attendance-register', { ...params, status: status || undefined })} />
        {counts.map((x) => (
          <button key={x.value} type="button" onClick={() => setStatus(status === x.value ? '' : x.value)} className={`rounded-full px-3 py-1.5 text-sm font-semibold ${status === x.value ? 'bg-brand-600 text-white' : 'border border-border bg-white text-muted'}`}>
            {x.label} {x.count}
          </button>
        ))}
      </div>
      {report.isError && <EmptyState title="Could not load the report" hint={apiErrorMessage(report.error, 'Check the date range and try again.')} />}
      {report.isLoading && <Loader />}
      {!report.isLoading && !report.isError && rows.length === 0 && <EmptyState title={status ? `No ${REGISTER_LABELS[status]?.toLowerCase()} entries in range` : 'No attendance in range'} />}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Date</th><th>Staff</th><th>Branch</th><th>Check In</th><th>Check Out</th><th>Status</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="text-muted">{r.workDate}</td>
                  <td><strong>{r.name}</strong><p className="text-xs text-muted">{r.employeeCode}</p></td>
                  <td className="text-muted">{r.branchName}</td>
                  <td className="text-muted">{time(r.checkInAt)}</td>
                  <td className="text-muted">{time(r.checkOutAt)}</td>
                  <td><span className={`badge ${REGISTER_BADGE[r.status] ?? ''}`}>{REGISTER_LABELS[r.status] ?? r.status.replace('_', ' ')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LeaveTab() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayDate());
  const [branchId, setBranchId] = useState('');
  const [regionId, setRegionId] = useState('');
  const params = { from, to, branchId: branchId || undefined, regionId: regionId || undefined };
  const report = useQuery({ queryKey: ['reports', 'leave', params], queryFn: async () => (await api.get<{ taken: LeaveTaken[]; balances: LeaveBalance[] }>('/api/reports/leave', { params })).data });
  const taken = report.data?.taken ?? [];
  const balances = report.data?.balances ?? [];
  return (
    <div>
      <div className="card mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="label">From<input type="date" className="field mt-1" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="label">To<input type="date" className="field mt-1" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <BranchRegionFilters branchId={branchId} setBranchId={setBranchId} regionId={regionId} setRegionId={setRegionId} />
      </div>
      <div className="mb-4"><ExportLink href={csvHref('/api/reports/leave', params)} /></div>
      {report.isLoading && <Loader />}

      <h2 className="mb-3 text-lg font-bold">Leave taken</h2>
      {!report.isLoading && taken.length === 0 && <EmptyState title="No approved leave in range" />}
      {taken.length > 0 && (
        <div className="table-wrap mb-8">
          <table className="table">
            <thead><tr><th>Staff</th><th>Branch</th><th>Leave Type</th><th>From</th><th>To</th><th>Days</th></tr></thead>
            <tbody>{taken.map((r, i) => <tr key={i}><td><strong>{r.name}</strong><p className="text-xs text-muted">{r.employeeCode}</p></td><td className="text-muted">{r.branchName}</td><td className="text-muted">{r.leaveType}</td><td className="text-muted">{r.startDate}</td><td className="text-muted">{r.endDate}</td><td>{r.days}</td></tr>)}</tbody>
          </table>
        </div>
      )}

      <h2 className="mb-3 text-lg font-bold">Leave balances</h2>
      {!report.isLoading && balances.length === 0 && <EmptyState title="No leave balances in scope" />}
      {balances.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Staff</th><th>Branch</th><th>Leave Type</th><th>Entitled</th><th>Carried Fwd</th><th>Used</th><th>Remaining</th></tr></thead>
            <tbody>{balances.map((r, i) => <tr key={i}><td><strong>{r.name}</strong><p className="text-xs text-muted">{r.employeeCode}</p></td><td className="text-muted">{r.branchName}</td><td className="text-muted">{r.leaveType}</td><td>{r.entitled}</td><td>{r.carriedForward}</td><td>{r.used}</td><td><strong>{r.remaining}</strong></td></tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HeadcountMovementTab() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayDate());
  const [branchId, setBranchId] = useState('');
  const [regionId, setRegionId] = useState('');
  const params = { from, to, branchId: branchId || undefined, regionId: regionId || undefined };
  const report = useQuery({ queryKey: ['reports', 'headcount-movement', params], queryFn: async () => (await api.get<{ joins: HeadcountJoin[]; exits: HeadcountExit[] }>('/api/reports/headcount-movement', { params })).data });
  const joins = report.data?.joins ?? [];
  const exits = report.data?.exits ?? [];
  return (
    <div>
      <div className="card mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="label">From<input type="date" className="field mt-1" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="label">To<input type="date" className="field mt-1" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <BranchRegionFilters branchId={branchId} setBranchId={setBranchId} regionId={regionId} setRegionId={setRegionId} />
      </div>
      <div className="mb-4"><ExportLink href={csvHref('/api/reports/headcount-movement', params)} /></div>
      {report.isLoading && <Loader />}

      <h2 className="mb-3 text-lg font-bold">Joined</h2>
      {!report.isLoading && joins.length === 0 && <EmptyState title="No new joiners in range" />}
      {joins.length > 0 && (
        <div className="table-wrap mb-8">
          <table className="table">
            <thead><tr><th>Staff</th><th>Branch</th><th>Joining Date</th></tr></thead>
            <tbody>{joins.map((r, i) => <tr key={i}><td><strong>{r.name}</strong><p className="text-xs text-muted">{r.employeeCode}</p></td><td className="text-muted">{r.branchName ?? '—'}</td><td className="text-muted">{r.joiningDate}</td></tr>)}</tbody>
          </table>
        </div>
      )}

      <h2 className="mb-3 text-lg font-bold">Exited</h2>
      {!report.isLoading && exits.length === 0 && <EmptyState title="No exits in range" />}
      {exits.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Staff</th><th>Branch</th><th>Exit Date</th></tr></thead>
            <tbody>{exits.map((r, i) => <tr key={i}><td><strong>{r.name}</strong><p className="text-xs text-muted">{r.employeeCode}</p></td><td className="text-muted">{r.branchName ?? '—'}</td><td className="text-muted">{r.exitDate}</td></tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SalaryRegisterTab() {
  const [fromMonth, setFromMonth] = useState(thisMonth());
  const [toMonth, setToMonth] = useState(thisMonth());
  const [branchId, setBranchId] = useState('');
  const [regionId, setRegionId] = useState('');
  const params = { from: `${fromMonth}-01`, to: `${toMonth}-01`, branchId: branchId || undefined, regionId: regionId || undefined };
  const report = useQuery({ queryKey: ['reports', 'salary-register', params], queryFn: async () => (await api.get<SalaryRow[]>('/api/reports/salary-register', { params })).data });
  const rows = report.data ?? [];
  return (
    <div>
      <div className="card mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="label">From month<input type="month" className="field mt-1" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} /></label>
        <label className="label">To month<input type="month" className="field mt-1" value={toMonth} onChange={(e) => setToMonth(e.target.value)} /></label>
        <BranchRegionFilters branchId={branchId} setBranchId={setBranchId} regionId={regionId} setRegionId={setRegionId} />
      </div>
      <div className="mb-4"><ExportLink href={csvHref('/api/reports/salary-register', params)} /></div>
      {report.isLoading && <Loader />}
      {!report.isLoading && rows.length === 0 && <EmptyState title="No locked/released payroll runs in range" hint="Only runs that have been locked or released are included." />}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Period</th><th>Staff</th><th>Branch</th><th>Payable Days</th><th>LOP Days</th><th>Gross</th><th>Deductions</th><th>Net Pay</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="text-muted">{monthLabel(r.periodMonth)}</td>
                  <td><strong>{r.name}</strong><p className="text-xs text-muted">{r.employeeCode}</p></td>
                  <td className="text-muted">{r.branchName ?? '—'}</td>
                  <td>{r.payableDays}</td><td>{r.lopDays}</td>
                  <td>{money(r.grossEarnings)}</td><td>{money(r.totalDeductions)}</td><td><strong>{money(r.netPay)}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LoanOutstandingTab() {
  const [branchId, setBranchId] = useState('');
  const [regionId, setRegionId] = useState('');
  const [type, setType] = useState('');
  const params = { branchId: branchId || undefined, regionId: regionId || undefined, type: type || undefined };
  const report = useQuery({ queryKey: ['reports', 'loan-outstanding', params], queryFn: async () => (await api.get<LoanRow[]>('/api/reports/loan-outstanding', { params })).data });
  const rows = report.data ?? [];
  const total = rows.reduce((sum, r) => sum + Number(r.balance), 0);
  return (
    <div>
      <div className="card mb-4 grid gap-3 sm:grid-cols-3">
        <BranchRegionFilters branchId={branchId} setBranchId={setBranchId} regionId={regionId} setRegionId={setRegionId} />
        <label className="label">Type<Select className="mt-1" value={type} onChange={(e) => setType(e.target.value)}><option value="">Loans & advances</option><option value="loan">Loans only</option><option value="advance">Advances only</option></Select></label>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <ExportLink href={csvHref('/api/reports/loan-outstanding', params)} />
        <span className="text-sm text-muted">Total outstanding: <strong className="text-body">{money(total.toFixed(2))}</strong></span>
      </div>
      {report.isLoading && <Loader />}
      {!report.isLoading && rows.length === 0 && <EmptyState title="No outstanding loans or advances" />}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Staff</th><th>Branch</th><th>Type</th><th>Principal</th><th>Balance</th><th>Status</th><th>Start Month</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td><strong>{r.name}</strong><p className="text-xs text-muted">{r.employeeCode}</p></td>
                  <td className="text-muted">{r.branchName}</td>
                  <td className="text-muted">{r.type}</td>
                  <td>{money(r.principalAmount)}</td><td><strong>{money(r.balance)}</strong></td>
                  <td><span className="badge">{r.status}</span></td>
                  <td className="text-muted">{r.startMonth}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatutorySummaryTab() {
  const [fromMonth, setFromMonth] = useState(thisMonth());
  const [toMonth, setToMonth] = useState(thisMonth());
  const [branchId, setBranchId] = useState('');
  const [regionId, setRegionId] = useState('');
  const params = { from: `${fromMonth}-01`, to: `${toMonth}-01`, branchId: branchId || undefined, regionId: regionId || undefined };
  const report = useQuery({ queryKey: ['reports', 'statutory-summary', params], queryFn: async () => (await api.get<StatutoryRow[]>('/api/reports/statutory-summary', { params })).data });
  const rows = report.data ?? [];
  return (
    <div>
      <div className="card mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="label">From month<input type="month" className="field mt-1" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} /></label>
        <label className="label">To month<input type="month" className="field mt-1" value={toMonth} onChange={(e) => setToMonth(e.target.value)} /></label>
        <BranchRegionFilters branchId={branchId} setBranchId={setBranchId} regionId={regionId} setRegionId={setRegionId} />
      </div>
      <div className="mb-4"><ExportLink href={csvHref('/api/reports/statutory-summary', params)} /></div>
      {report.isLoading && <Loader />}
      {!report.isLoading && rows.length === 0 && <EmptyState title="No locked/released payroll runs in range" />}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Period</th><th>Staff</th><th>PF (Emp)</th><th>PF (Er)</th><th>ESI (Emp)</th><th>ESI (Er)</th><th>PT</th><th>LWF (Emp)</th><th>LWF (Er)</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="text-muted">{monthLabel(r.periodMonth)}</td>
                  <td><strong>{r.name}</strong><p className="text-xs text-muted">{r.employeeCode}</p></td>
                  <td>{money(r.pfEmployeeAmount)}</td><td>{money(r.pfEmployerAmount)}</td>
                  <td>{money(r.esiEmployeeAmount)}</td><td>{money(r.esiEmployerAmount)}</td>
                  <td>{money(r.ptAmount)}</td>
                  <td>{money(r.lwfEmployeeAmount)}</td><td>{money(r.lwfEmployerAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ReportsPage() {
  const { user } = useAuth();
  const isPrivileged = !!user?.roles.some((r) => r === 'hr' || r === 'admin');
  const tabs = [
    ['attendance', 'Attendance Register', ClipboardList, AttendanceRegisterTab, true],
    ['leave', 'Leave', WalletCards, LeaveTab, true],
    ['headcount', 'Headcount Movement', UsersRound, HeadcountMovementTab, true],
    ['salary', 'Salary Register', CalendarClock, SalaryRegisterTab, isPrivileged],
    ['loans', 'Loan Outstanding', HandCoins, LoanOutstandingTab, isPrivileged],
    ['statutory', 'PF/ESI/PT Summary', WalletCards, StatutorySummaryTab, isPrivileged],
  ] as const;
  const visibleTabs = tabs.filter((t) => t[4]);
  const [tab, setTab] = useState<(typeof visibleTabs)[number][0]>('attendance');
  const Active = visibleTabs.find((t) => t[0] === tab)?.[3] ?? AttendanceRegisterTab;

  return (
    <>
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Reports</h1>
        <p className="mt-1 text-muted">Attendance, leave, salary, loans, and headcount — filtered and exportable to CSV.</p>
      </div>
      <div className="mb-6 flex flex-wrap gap-2 border-b border-border">
        {visibleTabs.map(([key, label, Icon]) => (
          <button key={key} className={`flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-semibold ${tab === key ? 'border-brand-600 text-brand-700' : 'border-transparent text-muted'}`} onClick={() => setTab(key)}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>
      <Active />
    </>
  );
}
